import { computed, Injectable, signal } from '@angular/core';
import { environment } from '@env/environment';
import { MockRule, NetworkEntry, NetworkEntryState, NetworkSession, ResponseTemplate, SavedRequest } from './network.types';

const KEY_CURRENT = 'devBar.network.currentSession';
const KEY_HISTORY = 'devBar.network.history';
const KEY_FAVORITES = 'devBar.network.favorites';
const KEY_MOCKS = 'devBar.network.mocks';
const KEY_SCHEMA = 'devBar.network.schemaVersion';
const KEY_ENABLED_METHODS = 'devBar.network.enabledMethods';
const KEY_ENABLED_STATES = 'devBar.network.enabledStates';
const KEY_ACTIVE_TAB = 'devBar.network.activeTab';
const KEY_VARIABLES = 'devBar.network.variables';
const KEY_TEMPLATES = 'devBar.network.templates';

const BUILT_IN_TEMPLATES: ResponseTemplate[] = [
  { id: '__401', name: '401 Unauthorized', status: 401, body: { error: 'Unauthorized', message: 'Authentication required' }, builtIn: true },
  { id: '__403', name: '403 Forbidden', status: 403, body: { error: 'Forbidden', message: 'Access denied' }, builtIn: true },
  { id: '__404', name: '404 Not Found', status: 404, body: { error: 'Not Found', message: 'Resource not found' }, builtIn: true },
  { id: '__500', name: '500 Server Error', status: 500, body: { error: 'Internal Server Error', message: 'Something went wrong' }, builtIn: true },
  { id: '__empty_list', name: 'Empty paginated list', status: 200, body: { data: [], total: 0, page: 1, pageSize: 10 }, builtIn: true },
  { id: '__empty_200', name: '200 Empty', status: 200, body: null, builtIn: true },
];
const SCHEMA_VERSION = 5;
const MAX_HISTORY_SESSIONS = 20;

/** HTTP verbs exposed as filter chips in the Network panel. */
export const NETWORK_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type NetworkMethod = typeof NETWORK_METHODS[number];

export const NETWORK_STATES: NetworkEntryState[] = ['success', 'error', 'pending', 'cancelled'];

@Injectable({ providedIn: 'root' })
export class NetworkMonitorService {
  private readonly _currentSession = signal<NetworkSession>({
    id: this.genId(),
    startedAt: Date.now(),
    entries: [],
  });
  readonly currentSession = this._currentSession.asReadonly();

  /** All history sessions stored in localStorage (loaded lazily). */
  private _allHistory: NetworkSession[] | null = null;
  /** Sessions loaded so far (most recent first). */
  private readonly _loadedHistory = signal<NetworkSession[]>([]);
  readonly loadedHistory = this._loadedHistory.asReadonly();
  /** Whether there are still older sessions that can be loaded. */
  readonly hasMoreHistory = signal(true);

  private readonly _favorites = signal<SavedRequest[]>([]);
  readonly favorites = this._favorites.asReadonly();

  private readonly _mocks = signal<MockRule[]>([]);
  readonly mocks = this._mocks.asReadonly();

  /** Environment variables for path/body substitution. */
  private readonly _variables = signal<Record<string, unknown>>({});
  readonly variables = this._variables.asReadonly();

  /** Response templates (built-in + custom). */
  private readonly _customTemplates = signal<ResponseTemplate[]>([]);
  readonly allTemplates = computed(() => [...BUILT_IN_TEMPLATES, ...this._customTemplates()]);

  /** Persisted active tab. */
  readonly activeTab = signal<string>(this.loadActiveTab());

  /** HTTP methods currently visible in the Network panel (persisted). */
  private readonly _enabledMethods = signal<Set<NetworkMethod>>(this.loadEnabledMethods());
  readonly enabledMethods = this._enabledMethods.asReadonly();

  /** Response states currently visible in the Network panel (persisted). */
  private readonly _enabledStates = signal<Set<NetworkEntryState>>(this.loadEnabledStates());
  readonly enabledStates = this._enabledStates.asReadonly();

  /** Re-emitted every 100ms so templates can recompute elapsed time on pending rows. */
  readonly tick = signal(Date.now());

  constructor() {
    if (environment.production) return;
    this.validateOrWipe();
    this.rotateSession();
    this.refreshPinnedFromHistory();
    this.loadFavorites();
    this.loadMocks();
    this.loadVariables();
    this.loadTemplates();
    setInterval(() => this.tick.set(Date.now()), 100);
  }

  // ── Filter state ───────────────────────────────────────────────────────────

  toggleMethod(method: NetworkMethod): void {
    this._enabledMethods.update(set => {
      const next = new Set(set);
      if (next.has(method)) next.delete(method);
      else next.add(method);
      return next;
    });
    this.persistEnabledMethods();
  }

  toggleState(state: NetworkEntryState): void {
    this._enabledStates.update(set => {
      const next = new Set(set);
      if (next.has(state)) next.delete(state);
      else next.add(state);
      return next;
    });
    this.persistEnabledStates();
  }

  setAllMethods(enabled: boolean): void {
    this._enabledMethods.set(enabled ? new Set(NETWORK_METHODS) : new Set());
    this.persistEnabledMethods();
  }

  setAllStates(enabled: boolean): void {
    this._enabledStates.set(enabled ? new Set(NETWORK_STATES) : new Set());
    this.persistEnabledStates();
  }

  /** Checks whether a network entry passes the current method + state filters. */
  passesFilters(entry: NetworkEntry): boolean {
    const methodKey = entry.method.toUpperCase() as NetworkMethod;
    const methodOk = NETWORK_METHODS.includes(methodKey)
      ? this._enabledMethods().has(methodKey)
      : true; // unknown verb — don't hide it accidentally
    return methodOk && this._enabledStates().has(entry.state);
  }

  private loadEnabledMethods(): Set<NetworkMethod> {
    if (environment.production) return new Set(NETWORK_METHODS);
    const raw = localStorage.getItem(KEY_ENABLED_METHODS);
    if (!raw) return new Set(NETWORK_METHODS);
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set(NETWORK_METHODS);
      const valid = parsed.filter((v): v is NetworkMethod =>
        (NETWORK_METHODS as readonly string[]).includes(v)
      );
      return new Set(valid);
    } catch {
      return new Set(NETWORK_METHODS);
    }
  }

  private loadEnabledStates(): Set<NetworkEntryState> {
    if (environment.production) return new Set(NETWORK_STATES);
    const raw = localStorage.getItem(KEY_ENABLED_STATES);
    if (!raw) return new Set(NETWORK_STATES);
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set(NETWORK_STATES);
      const valid = parsed.filter((v): v is NetworkEntryState =>
        NETWORK_STATES.includes(v as NetworkEntryState)
      );
      return new Set(valid);
    } catch {
      return new Set(NETWORK_STATES);
    }
  }

  private persistEnabledMethods(): void {
    try {
      localStorage.setItem(KEY_ENABLED_METHODS, JSON.stringify([...this._enabledMethods()]));
    } catch {/* quota */}
  }

  private persistEnabledStates(): void {
    try {
      localStorage.setItem(KEY_ENABLED_STATES, JSON.stringify([...this._enabledStates()]));
    } catch {/* quota */}
  }

  // ── Schema validation ──────────────────────────────────────────────────────

  /** Wipes all network localStorage keys if schema mismatch or any data is malformed. */
  private validateOrWipe(): void {
    const storedVersion = parseInt(localStorage.getItem(KEY_SCHEMA) ?? '0', 10);
    if (storedVersion !== SCHEMA_VERSION) {
      this.wipeAll();
      return;
    }

    const checks: Array<[string, (v: unknown) => boolean]> = [
      [KEY_CURRENT, v => this.isValidSession(v)],
      [KEY_HISTORY, v => Array.isArray(v) && v.every(s => this.isValidSession(s))],
      [KEY_FAVORITES, v => Array.isArray(v) && v.every(f => this.isValidFavorite(f))],
    ];

    for (const [key, validate] of checks) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        if (!validate(JSON.parse(raw))) {
          this.wipeAll();
          return;
        }
      } catch {
        this.wipeAll();
        return;
      }
    }
  }

  private wipeAll(): void {
    localStorage.removeItem(KEY_CURRENT);
    localStorage.removeItem(KEY_HISTORY);
    localStorage.removeItem(KEY_FAVORITES);
    localStorage.removeItem(KEY_MOCKS);
    localStorage.removeItem(KEY_VARIABLES);
    localStorage.setItem(KEY_SCHEMA, String(SCHEMA_VERSION));
  }

  private isValidSession(s: unknown): boolean {
    if (!s || typeof s !== 'object') return false;
    const o = s as Record<string, unknown>;
    return (
      typeof o['id'] === 'string' &&
      typeof o['startedAt'] === 'number' &&
      Array.isArray(o['entries']) &&
      (o['entries'] as unknown[]).every(e => this.isValidEntry(e))
    );
  }

  private isValidEntry(e: unknown): boolean {
    if (!e || typeof e !== 'object') return false;
    const o = e as Record<string, unknown>;
    return (
      typeof o['id'] === 'string' &&
      typeof o['method'] === 'string' &&
      typeof o['url'] === 'string' &&
      typeof o['path'] === 'string' &&
      typeof o['startedAt'] === 'number' &&
      (o['state'] === 'pending' || o['state'] === 'success' || o['state'] === 'error' || o['state'] === 'cancelled')
    );
  }

  private isValidFavorite(f: unknown): boolean {
    if (!f || typeof f !== 'object') return false;
    const o = f as Record<string, unknown>;
    return (
      typeof o['id'] === 'string' &&
      typeof o['name'] === 'string' &&
      typeof o['method'] === 'string' &&
      typeof o['path'] === 'string' &&
      typeof o['savedAt'] === 'number'
    );
  }

  // ── Current session ────────────────────────────────────────────────────────

  add(entry: NetworkEntry): void {
    this._currentSession.update(s => ({ ...s, entries: [...s.entries, entry] }));
    this.persistCurrentSession();
  }

  complete(id: string, status: number, statusText: string, response: unknown, mocked = false): void {
    const now = Date.now();
    this._currentSession.update(s => ({
      ...s,
      entries: s.entries.map(e => {
        if (e.id !== id) return e;
        const responseTimeMs = now - e.startedAt;
        return {
          ...e,
          finishedAt: now,
          status,
          statusText,
          response,
          state: (status >= 200 && status < 300 ? 'success' : 'error') as NetworkEntryState,
          mocked,
          responseTimeMs,
          responseSize: estimatePayloadSize(response),
        };
      }),
    }));
    this.persistCurrentSession();
  }

  cancel(id: string): void {
    this._currentSession.update(s => ({
      ...s,
      entries: s.entries.map(e =>
        e.id === id && e.state === 'pending'
          ? { ...e, finishedAt: Date.now(), state: 'cancelled' as const }
          : e
      ),
    }));
    this.persistCurrentSession();
  }

  /** Reset an entry back to pending for a retry. */
  resetEntryForRetry(id: string): void {
    this._currentSession.update(s => ({
      ...s,
      entries: s.entries.map(e =>
        e.id === id
          ? { ...e, state: 'pending' as NetworkEntryState, startedAt: Date.now(), finishedAt: undefined, status: undefined, statusText: undefined, response: undefined, mocked: false, responseTimeMs: undefined, responseSize: undefined }
          : e
      ),
    }));
    this.persistCurrentSession();
  }

  /** Complete a retried entry (updates in place instead of creating a new entry). */
  completeRetry(id: string, status: number, statusText: string, response: unknown): void {
    this.complete(id, status, statusText, response, false);
  }

  updateEntryBody(id: string, body: unknown): void {
    this._currentSession.update(s => ({
      ...s,
      entries: s.entries.map(e => e.id === id ? { ...e, requestBody: body } : e),
    }));
    this.persistCurrentSession();
  }

  remove(id: string): void {
    this._currentSession.update(s => ({ ...s, entries: s.entries.filter(e => e.id !== id) }));
    this.persistCurrentSession();
  }

  togglePin(id: string): void {
    // Try current session first
    const inCurrent = this._currentSession().entries.some(e => e.id === id);
    if (inCurrent) {
      this._currentSession.update(s => ({
        ...s,
        entries: s.entries.map(e => e.id === id ? { ...e, pinned: !e.pinned } : e),
      }));
      this.persistCurrentSession();
      return;
    }
    // Otherwise toggle in loaded history
    this._loadedHistory.update(sessions =>
      sessions.map(s => ({
        ...s,
        entries: s.entries.map(e => e.id === id ? { ...e, pinned: !e.pinned } : e),
      }))
    );
    // Sync back to allHistory and persist
    if (this._allHistory) {
      this._allHistory = this._allHistory.map(s => ({
        ...s,
        entries: s.entries.map(e => e.id === id ? { ...e, pinned: !e.pinned } : e),
      }));
    }
    try {
      localStorage.setItem(KEY_HISTORY, JSON.stringify(this._allHistory ?? []));
    } catch {/* quota */}
    this.refreshPinnedFromHistory();
  }

  clearCurrent(): void {
    // Keep only pinned entries in the current session
    this._currentSession.update(s => ({ ...s, entries: s.entries.filter(e => e.pinned) }));
    this.persistCurrentSession();
    // Wipe all history (loaded + stored)
    this._loadedHistory.set([]);
    this._allHistory = [];
    this.hasMoreHistory.set(false);
    try { localStorage.setItem(KEY_HISTORY, '[]'); } catch {/* quota */}
    this._pinnedFromHistory.set([]);
  }


  // ── History ────────────────────────────────────────────────────────────────

  /** Load one more historic session (most recent first). */
  loadOneMoreSession(): void {
    if (!this._allHistory) {
      const raw = localStorage.getItem(KEY_HISTORY);
      this._allHistory = raw ? this.safeParse<NetworkSession[]>(raw, []) : [];
    }
    const loaded = this._loadedHistory();
    const nextIndex = this._allHistory.length - 1 - loaded.length;
    if (nextIndex >= 0) {
      // Prepend older session so it appears above
      this._loadedHistory.set([this._allHistory[nextIndex], ...loaded]);
    }
    this.hasMoreHistory.set(nextIndex > 0);
    this.refreshPinnedFromHistory();
  }

  /** Pinned entries from history sessions that haven't been explicitly loaded.
   *  Always visible in the monitor tab regardless of session loading state. */
  private readonly _pinnedFromHistory = signal<NetworkEntry[]>([]);
  readonly pinnedFromHistory = this._pinnedFromHistory.asReadonly();

  /** Extract pinned entries from all unloaded history sessions. */
  private refreshPinnedFromHistory(): void {
    if (!this._allHistory) {
      const raw = localStorage.getItem(KEY_HISTORY);
      this._allHistory = raw ? this.safeParse<NetworkSession[]>(raw, []) : [];
    }
    const loadedIds = new Set(this._loadedHistory().map(s => s.id));
    const pinned = this._allHistory
      .filter(s => !loadedIds.has(s.id))
      .flatMap(s => s.entries.filter(e => e.pinned));
    this._pinnedFromHistory.set(pinned);
  }

  removeHistoricEntry(sessionId: string, entryId: string): void {
    this._loadedHistory.update(sessions =>
      sessions
        .map(s => (s.id === sessionId ? { ...s, entries: s.entries.filter(e => e.id !== entryId) } : s))
        .filter(s => s.entries.length > 0)
    );
    // Sync back to allHistory and persist
    if (this._allHistory) {
      this._allHistory = this._allHistory
        .map(s => (s.id === sessionId ? { ...s, entries: s.entries.filter(e => e.id !== entryId) } : s))
        .filter(s => s.entries.length > 0);
    }
    try {
      localStorage.setItem(KEY_HISTORY, JSON.stringify(this._allHistory ?? []));
    } catch {/* quota */}
  }

  // ── Favorites ──────────────────────────────────────────────────────────────

  addFavorite(req: Omit<SavedRequest, 'id' | 'savedAt'>): void {
    const full: SavedRequest = { ...req, id: this.genId(), savedAt: Date.now() };
    this._favorites.update(f => [full, ...f]);
    this.persistFavorites();
  }

  updateFavorite(id: string, changes: Partial<SavedRequest>): void {
    this._favorites.update(f => f.map(r => (r.id === id ? { ...r, ...changes } : r)));
    this.persistFavorites();
  }

  removeFavorite(id: string): void {
    this._favorites.update(f => f.filter(r => r.id !== id));
    this.persistFavorites();
  }

  duplicateFavorite(id: string): void {
    const orig = this._favorites().find(f => f.id === id);
    if (!orig) return;
    const clone: SavedRequest = {
      ...orig,
      id: this.genId(),
      name: orig.name + ' (copy)',
      savedAt: Date.now(),
      lastResponse: undefined,
      lastResponseStatus: undefined,
      lastResponseAt: undefined,
    };
    // Insert right after the original
    this._favorites.update(f => {
      const idx = f.findIndex(r => r.id === id);
      const next = [...f];
      next.splice(idx + 1, 0, clone);
      return next;
    });
    this.persistFavorites();
  }

  reorderFavorites(ids: string[]): void {
    const map = new Map(this._favorites().map(f => [f.id, f]));
    const reordered = ids.map(id => map.get(id)).filter(Boolean) as SavedRequest[];
    // Append any not in ids (shouldn't happen, safety net)
    for (const f of this._favorites()) {
      if (!ids.includes(f.id)) reordered.push(f);
    }
    this._favorites.set(reordered);
    this.persistFavorites();
  }

  exportFavorites(): string {
    return JSON.stringify(this._favorites(), null, 2);
  }

  importFavorites(data: SavedRequest[]): void {
    // Assign new IDs to avoid collisions
    const imported = data.map(f => ({
      ...f,
      id: this.genId(),
      savedAt: f.savedAt || Date.now(),
    }));
    this._favorites.update(existing => [...existing, ...imported]);
    this.persistFavorites();
  }

  // ── Variables (for request chaining & env vars) ───────────────────────────

  setVariable(name: string, value: unknown): void {
    this._variables.update(v => ({ ...v, [name]: value }));
    this.persistVariables();
  }

  removeVariable(name: string): void {
    this._variables.update(v => {
      const next = { ...v };
      delete next[name];
      return next;
    });
    this.persistVariables();
  }

  clearVariables(): void {
    this._variables.set({});
    this.persistVariables();
  }

  /**
   * Resolve variables in a string: {{varName}} and $alias.path syntax.
   * Returns the resolved string.
   */
  resolveVariables(text: string): string {
    const vars = this._variables();
    // Replace {{varName}}
    let result = text.replace(/\{\{(\w+)\}\}/g, (_, name) => {
      const val = vars[name];
      return val !== undefined ? String(val) : `{{${name}}}`;
    });
    // Replace $alias.path.to.field or $alias[0].field
    result = result.replace(/\$(\w+)((?:\.\w+|\[\d+\])*)/g, (match, alias, accessors) => {
      const root = vars[alias];
      if (root === undefined) return match;
      if (!accessors) return typeof root === 'object' ? JSON.stringify(root) : String(root);
      try {
        let current: unknown = root;
        const parts = accessors.match(/\.(\w+)|\[(\d+)\]/g) || [];
        for (const part of parts) {
          if (current === null || current === undefined) return match;
          if (part.startsWith('.')) {
            current = (current as Record<string, unknown>)[part.slice(1)];
          } else {
            const idx = parseInt(part.slice(1, -1), 10);
            current = (current as unknown[])[idx];
          }
        }
        return typeof current === 'object' ? JSON.stringify(current) : String(current ?? match);
      } catch {
        return match;
      }
    });
    return result;
  }

  /** Resolve variables in a JSON body (deep string replacement). */
  resolveBodyVariables(body: unknown): unknown {
    if (body === null || body === undefined) return body;
    if (typeof body === 'string') return this.resolveVariables(body);
    if (Array.isArray(body)) return body.map(item => this.resolveBodyVariables(item));
    if (typeof body === 'object') {
      const result: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(body as Record<string, unknown>)) {
        result[this.resolveVariables(k)] = this.resolveBodyVariables(v);
      }
      return result;
    }
    return body;
  }

  private loadVariables(): void {
    const raw = localStorage.getItem(KEY_VARIABLES);
    if (raw) this._variables.set(this.safeParse<Record<string, unknown>>(raw, {}));
  }

  private persistVariables(): void {
    try { localStorage.setItem(KEY_VARIABLES, JSON.stringify(this._variables())); } catch {/* quota */}
  }

  // ── Mocks ─────────────────────────────────────────────────────────────────

  addMock(rule: Omit<MockRule, 'id' | 'createdAt'>): void {
    const full: MockRule = { ...rule, id: this.genId(), createdAt: Date.now() };
    this._mocks.update(m => [full, ...m]);
    this.persistMocks();
  }

  updateMock(id: string, changes: Partial<MockRule>): void {
    this._mocks.update(m => m.map(r => (r.id === id ? { ...r, ...changes } : r)));
    this.persistMocks();
  }

  removeMock(id: string): void {
    this._mocks.update(m => m.filter(r => r.id !== id));
    this.persistMocks();
  }

  duplicateMock(id: string): void {
    const orig = this._mocks().find(m => m.id === id);
    if (!orig) return;
    const clone: MockRule = {
      ...orig,
      id: this.genId(),
      name: orig.name + ' (copy)',
      createdAt: Date.now(),
      hitCount: 0,
    };
    this._mocks.update(m => {
      const idx = m.findIndex(r => r.id === id);
      const next = [...m];
      next.splice(idx + 1, 0, clone);
      return next;
    });
    this.persistMocks();
  }

  reorderMocks(ids: string[]): void {
    const map = new Map(this._mocks().map(m => [m.id, m]));
    const reordered = ids.map(id => map.get(id)).filter(Boolean) as MockRule[];
    for (const m of this._mocks()) {
      if (!ids.includes(m.id)) reordered.push(m);
    }
    this._mocks.set(reordered);
    this.persistMocks();
  }

  bulkToggleMocks(enabled: boolean): void {
    this._mocks.update(m => m.map(r => ({ ...r, enabled })));
    this.persistMocks();
  }

  incrementMockHit(id: string): void {
    this._mocks.update(m => m.map(r =>
      r.id === id ? { ...r, hitCount: (r.hitCount ?? 0) + 1 } : r
    ));
    this.persistMocks();
  }

  resetAllMockHits(): void {
    this._mocks.update(m => m.map(r => ({ ...r, hitCount: 0 })));
    this.persistMocks();
  }

  exportMocks(): string {
    return JSON.stringify(this._mocks(), null, 2);
  }

  importMocks(data: MockRule[]): void {
    const imported = data.map(m => ({ ...m, id: this.genId(), createdAt: m.createdAt || Date.now(), hitCount: 0 }));
    this._mocks.update(existing => [...existing, ...imported]);
    this.persistMocks();
  }

  importSession(data: unknown): void {
    if (!data || typeof data !== 'object') return;
    const obj = data as Record<string, unknown>;
    // Accept both the export format { currentSession, historicSessions } and raw session arrays
    const sessions: NetworkSession[] = [];
    if (obj['currentSession'] && typeof obj['currentSession'] === 'object') {
      const s = obj['currentSession'] as NetworkSession;
      if (s.entries && Array.isArray(s.entries)) sessions.push(s);
    }
    if (Array.isArray(obj['historicSessions'])) {
      for (const s of obj['historicSessions']) {
        if (s && s.entries && Array.isArray(s.entries)) sessions.push(s as NetworkSession);
      }
    }
    if (sessions.length === 0 && Array.isArray(data)) {
      for (const s of data) {
        if (s && s.entries && Array.isArray(s.entries)) sessions.push(s as NetworkSession);
      }
    }
    if (sessions.length === 0) return;
    // Assign new IDs and add as loaded history
    const imported = sessions.map(s => ({
      ...s,
      id: this.genId(),
      entries: s.entries.map(e => ({ ...e, id: this.genId() })),
    }));
    this._loadedHistory.update(existing => [...imported, ...existing]);
    // Also persist into allHistory
    if (!this._allHistory) {
      const raw = localStorage.getItem(KEY_HISTORY);
      this._allHistory = raw ? this.safeParse<NetworkSession[]>(raw, []) : [];
    }
    this._allHistory = [...this._allHistory, ...imported];
    try { localStorage.setItem(KEY_HISTORY, JSON.stringify(this._allHistory)); } catch {/* quota */}
    this.refreshPinnedFromHistory();
  }

  /** Create a mock rule pre-filled from a captured request. */
  createMockFromEntry(entry: NetworkEntry): void {
    this.addMock({
      name: `Mock ${entry.method} ${entry.path}`,
      enabled: true,
      method: entry.method,
      path: entry.path,
      queryFilters: [],
      requestFilterFn: '',
      mockResponse: entry.response ?? null,
      mockStatus: entry.status ?? 200,
      delay: 50,
      hitCount: 0,
      mapFn: '',
    });
  }

  // ── Templates ────────────────────────────────────────────────────────────

  addTemplate(t: Omit<ResponseTemplate, 'id'>): void {
    this._customTemplates.update(ts => [...ts, { ...t, id: this.genId() }]);
    this.persistTemplates();
  }

  removeTemplate(id: string): void {
    this._customTemplates.update(ts => ts.filter(t => t.id !== id));
    this.persistTemplates();
  }

  private loadTemplates(): void {
    const raw = localStorage.getItem(KEY_TEMPLATES);
    if (raw) this._customTemplates.set(this.safeParse<ResponseTemplate[]>(raw, []));
  }

  private persistTemplates(): void {
    try { localStorage.setItem(KEY_TEMPLATES, JSON.stringify(this._customTemplates())); } catch {/* quota */}
  }

  /** Returns the first active mock rule matching a request, or null. */
  findMatchingMock(method: string, path: string, body: unknown): MockRule | null {
    for (const rule of this._mocks()) {
      if (!rule.enabled) continue;
      if (rule.method && rule.method.toUpperCase() !== method.toUpperCase()) continue;
      if (!this.matchPath(rule, path)) continue;
      if (!this.matchQueryFilters(rule, path)) continue;
      if (!this.matchRequestFilter(rule, body)) continue;
      return rule;
    }
    return null;
  }

  private matchPath(rule: MockRule, requestPath: string): boolean {
    if (!rule.path) return true;
    // Strip query string for path matching
    const pathOnly = requestPath.split('?')[0];
    const rulePathOnly = rule.path.split('?')[0];
    // Convert wildcard pattern to regex: * matches any segment chars (not /)
    const escaped = rulePathOnly.replace(/[.+^${}()|[\]\\]/g, '\\$&');
    const pattern = escaped.replace(/\*/g, '[^/]+');
    const regex = new RegExp('^/?' + pattern + '/?$', 'i');
    const normalized = pathOnly.startsWith('/') ? pathOnly : '/' + pathOnly;
    return regex.test(normalized);
  }

  private matchQueryFilters(rule: MockRule, requestPath: string): boolean {
    if (!rule.queryFilters || rule.queryFilters.length === 0) return true;
    const qIdx = requestPath.indexOf('?');
    const params = new URLSearchParams(qIdx >= 0 ? requestPath.substring(qIdx) : '');
    return rule.queryFilters.every(f => {
      const key = f.key.trim();
      if (!key) return true;
      const comparator = f.comparator || 'eq';
      const actual = params.get(key);
      if (comparator === 'exists') return actual !== null;
      if (actual === null) return false;
      switch (comparator) {
        case 'eq':       return actual === f.value;
        case 'neq':      return actual !== f.value;
        case 'contains': return actual.includes(f.value);
        case 'starts':   return actual.startsWith(f.value);
        case 'ends':     return actual.endsWith(f.value);
        case 'regex':    try { return new RegExp(f.value).test(actual); } catch { return false; }
        default:         return actual === f.value;
      }
    });
  }

  private matchRequestFilter(rule: MockRule, body: unknown): boolean {
    const fn = rule.requestFilterFn?.trim();
    if (!fn) return true;
    try {
      const filterFn = new Function('request', fn) as (req: unknown) => boolean;
      return !!filterFn(body);
    } catch {
      return true; // if the function is invalid, don't block
    }
  }

  // ── Active tab persistence ────────────────────────────────────────────────

  setActiveTab(tab: string): void {
    this.activeTab.set(tab);
    try { localStorage.setItem(KEY_ACTIVE_TAB, tab); } catch {/* quota */}
  }

  private loadActiveTab(): string {
    if (environment.production) return 'monitor';
    return localStorage.getItem(KEY_ACTIVE_TAB) || 'monitor';
  }

  // ── Persistence ────────────────────────────────────────────────────────────

  /** Move the previously-running "current session" (if any) into the history ring. */
  private rotateSession(): void {
    const raw = localStorage.getItem(KEY_CURRENT);
    if (raw) {
      try {
        const prev: NetworkSession = JSON.parse(raw);
        if (prev.entries.length > 0) {
          const historyRaw = localStorage.getItem(KEY_HISTORY);
          const history: NetworkSession[] = historyRaw ? JSON.parse(historyRaw) : [];
          history.push(prev);
          localStorage.setItem(KEY_HISTORY, JSON.stringify(history.slice(-MAX_HISTORY_SESSIONS)));
        }
      } catch {/* malformed previous session — drop */}
    }
    this.persistCurrentSession();
  }

  private loadFavorites(): void {
    const raw = localStorage.getItem(KEY_FAVORITES);
    if (raw) {
      this._favorites.set(this.safeParse<SavedRequest[]>(raw, []));
    }
  }

  private loadMocks(): void {
    const raw = localStorage.getItem(KEY_MOCKS);
    if (raw) {
      this._mocks.set(this.safeParse<MockRule[]>(raw, []));
    }
  }

  private persistMocks(): void {
    try {
      localStorage.setItem(KEY_MOCKS, JSON.stringify(this._mocks()));
    } catch {/* quota */}
  }

  private persistCurrentSession(): void {
    try {
      localStorage.setItem(KEY_CURRENT, JSON.stringify(this._currentSession()));
    } catch {/* quota — drop silently */}
  }

  private persistFavorites(): void {
    try {
      localStorage.setItem(KEY_FAVORITES, JSON.stringify(this._favorites()));
    } catch {/* quota */}
  }

  private safeParse<T>(raw: string, fallback: T): T {
    try { return JSON.parse(raw) as T; } catch { return fallback; }
  }

  private genId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : String(Date.now()) + Math.random().toString(36).slice(2);
  }
}

function estimatePayloadSize(payload: unknown): number {
  if (payload === null || payload === undefined) return 0;
  if (typeof payload === 'string') return payload.length;
  try { return JSON.stringify(payload).length; } catch { return 0; }
}
